// pages/AuctionPage.js
import { AlertCircle, ArrowLeft, Clock, DollarSign, Gavel, ShoppingCart, Users } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import axios from '../utils/axiosHandler';

const AuctionPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, login } = useAuth();
    const { config } = useConfig();

    const [auction, setAuction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showBidModal, setShowBidModal] = useState(false);
    const [showBuyNowModal, setShowBuyNowModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [bidAmount, setBidAmount] = useState('');
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // New auction login flow state
    const [loginStep, setLoginStep] = useState('identifier'); // identifier, password, verification, register-phone, register-email, verify-new-phone, verify-registration-phone
    const [loginData, setLoginData] = useState({
        identifier: '',
        verificationCode: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
    });
    const [loginResponse, setLoginResponse] = useState(null);
    const [pendingAction, setPendingAction] = useState(null); // 'bid' or 'buyNow'

    // Ref to track if bid amount has been set by user
    const bidAmountSetByUser = useRef(false);

    // Function to rebind auction buttons
    const rebindAuctionButtons = () => {
        setTimeout(() => {
            const bidButtons = document.querySelectorAll('[id^="auction-bid-btn"]');
            const buyNowButtons = document.querySelectorAll('[id^="auction-buynow-btn"]');

            console.log('Rebinding bid buttons:', bidButtons.length);
            console.log('Rebinding buy now buttons:', buyNowButtons.length);

            // Store event listener functions for proper cleanup
            const bidClickHandler = (e) => {
                e.preventDefault();
                console.log('Bid button clicked!');
                setShowBidModal(true);
            };

            const buyNowClickHandler = (e) => {
                e.preventDefault();
                console.log('Buy now button clicked!');
                if (!user) {
                    setPendingAction('buyNow');
                    setShowLoginModal(true);
                    return;
                }
                setShowBuyNowModal(true);
            };

            bidButtons.forEach(button => {
                console.log('Binding bid button:', button.id);
                // Remove any existing listeners first
                if (button._bidClickHandler) {
                    button.removeEventListener('click', button._bidClickHandler);
                }
                button.addEventListener('click', bidClickHandler);
                // Store the handler reference for cleanup
                button._bidClickHandler = bidClickHandler;
            });

            buyNowButtons.forEach(button => {
                console.log('Binding buy now button:', button.id);
                // Remove any existing listeners first
                if (button._buyNowClickHandler) {
                    button.removeEventListener('click', button._buyNowClickHandler);
                }
                button.addEventListener('click', buyNowClickHandler);
                // Store the handler reference for cleanup
                button._buyNowClickHandler = buyNowClickHandler;
            });
        }, 100);
    };

    useEffect(() => {
        fetchAuction();
        const interval = setInterval(fetchAuction, 30000); // Update every 30 seconds
        return () => clearInterval(interval);
    }, [id]);

    useEffect(() => {
        if (auction) {
            // Calculate time remaining
            const endTime = new Date(auction.endDate).getTime();
            const now = new Date().getTime();
            const remaining = Math.max(0, endTime - now);
            setTimeRemaining(remaining);

            // Set initial bid amount to next minimum bid only if bid amount is empty
            if (!bidAmount && !bidAmountSetByUser.current) {
                setBidAmount(auction.nextMinimumBid?.toString() || '');
            }

            // Set up auction button handlers globally
            window.showBidModal = (auctionId) => {
                setShowBidModal(true);
            };

            window.showBuyNowModal = (auctionId) => {
                if (!user) {
                    setPendingAction('buyNow');
                    setShowBuyNowModal(true); // Show buy now modal first
                    return;
                }
                setShowBuyNowModal(true);
            };

            // Bind event listeners to buttons in the rich text content
            setTimeout(() => {
                const bidButtons = document.querySelectorAll('[id^="auction-bid-btn"]');
                const buyNowButtons = document.querySelectorAll('[id^="auction-buynow-btn"]');

                console.log('Found bid buttons:', bidButtons.length);
                console.log('Found buy now buttons:', buyNowButtons.length);

                // Store event listener functions for proper cleanup
                const bidClickHandler = (e) => {
                    e.preventDefault();
                    console.log('Bid button clicked!');
                    setShowBidModal(true);
                };

                const buyNowClickHandler = (e) => {
                    e.preventDefault();
                    console.log('Buy now button clicked!');
                    if (!user) {
                        setPendingAction('buyNow');
                        setShowBuyNowModal(true); // Show buy now modal first
                        return;
                    }
                    setShowBuyNowModal(true);
                };

                bidButtons.forEach(button => {
                    console.log('Binding bid button:', button.id);
                    // Remove any existing listeners first
                    if (button._bidClickHandler) {
                        button.removeEventListener('click', button._bidClickHandler);
                    }
                    button.addEventListener('click', bidClickHandler);
                    // Store the handler reference for cleanup
                    button._bidClickHandler = bidClickHandler;
                });

                buyNowButtons.forEach(button => {
                    console.log('Binding buy now button:', button.id);
                    // Remove any existing listeners first
                    if (button._buyNowClickHandler) {
                        button.removeEventListener('click', button._buyNowClickHandler);
                    }
                    button.addEventListener('click', buyNowClickHandler);
                    // Store the handler reference for cleanup
                    button._buyNowClickHandler = buyNowClickHandler;
                });
            }, 100); // Small delay to ensure content is rendered
        }

        return () => {
            // Clean up global handlers
            delete window.showBidModal;
            delete window.showBuyNowModal;

            // Clean up event listeners
            const bidButtons = document.querySelectorAll('[id^="auction-bid-btn"]');
            const buyNowButtons = document.querySelectorAll('[id^="auction-buynow-btn"]');

            bidButtons.forEach(button => {
                if (button._bidClickHandler) {
                    button.removeEventListener('click', button._bidClickHandler);
                    delete button._bidClickHandler;
                }
            });

            buyNowButtons.forEach(button => {
                if (button._buyNowClickHandler) {
                    button.removeEventListener('click', button._buyNowClickHandler);
                    delete button._buyNowClickHandler;
                }
            });
        };
    }, [auction, user]);

    const fetchAuction = async () => {
        try {
            const response = await axios.get(`/api/auctions/public/${id}`);

            if (response.status === 200) {
                const data = response.data;
                setAuction(data);
            } else {
                setError('Auction not found');
            }
        } catch (error) {
            console.error('Error fetching auction:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Error loading auction';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handlePlaceBid = async () => {
        if (!bidAmount || parseInt(bidAmount) < auction.nextMinimumBid) {
            setError(`Bid must be at least $${auction.nextMinimumBid?.toLocaleString()}`);
            return;
        }

        if (!user) {
            setPendingAction('bid');
            setShowBidModal(false);
            setShowLoginModal(true);
            return;
        }

        try {
            const response = await axios.post(`/api/auctions/${id}/bid`, {
                bidAmount: parseInt(bidAmount)
            });

            const data = response.data;

            if (response.status === 200 || response.status === 201) {
                setSuccess(data.message);
                setShowBidModal(false);
                fetchAuction(); // Refresh auction data
                setBidAmount(''); // Reset bid amount
                setPendingAction(null); // Clear pending action after successful bid
                bidAmountSetByUser.current = false; // Reset bid amount tracking
                rebindAuctionButtons();
            } else {
                setError(data.error || 'Error placing bid');
            }
        } catch (error) {
            console.error('Error placing bid:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Error placing bid';
            setError(errorMessage);
        }
    };

    const handleBuyNow = async () => {
        if (!user) {
            setPendingAction('buyNow');
            setShowBuyNowModal(false);
            setShowLoginModal(true);
            return;
        }

        try {
            const response = await axios.post(`/api/auctions/${id}/buy-now`);

            const data = response.data;

            if (response.status === 200 || response.status === 201) {
                setSuccess(data.message);
                setShowBuyNowModal(false);
                fetchAuction(); // Refresh auction data
                setPendingAction(null); // Clear pending action after successful purchase
                rebindAuctionButtons();
            } else {
                setError(data.error || 'Error processing purchase');
            }
        } catch (error) {
            console.error('Error processing buy now:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Error processing purchase';
            setError(errorMessage);
        }
    };

    // New auction login flow functions
    const handleIdentifierSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/auction/login', {
                identifier: loginData.identifier
            });

            const data = response.data;

            if (response.status === 200) {
                setLoginResponse(data);

                if (data.exists) {
                    if (data.requiresVerification) {
                        setLoginStep('verification');
                    } else {
                        // Direct login successful
                        localStorage.setItem('token', data.token);
                        localStorage.setItem('user', JSON.stringify(data.user));
                        login(data.user, data.token, true); // Pass noRedirect: true
                        setShowLoginModal(false);
                        resetLoginState();

                        // Execute pending action if any
                        if (pendingAction === 'bid') {
                            setShowBidModal(true);
                        } else if (pendingAction === 'buyNow') {
                            setShowBuyNowModal(true);
                        }
                    }
                } else {
                    // User doesn't exist - show registration
                    if (data.identifierType === 'phone') {
                        if (data.requiresVerification) {
                            // Phone verification code was sent, show verification step
                            setLoginData(prev => ({ ...prev, phone: loginData.identifier }));
                            setLoginStep('verify-new-phone');
                        } else {
                            // Phone provided, need to verify it first, then ask for email and names
                            setLoginData(prev => ({ ...prev, phone: loginData.identifier }));
                            setLoginStep('verify-new-phone');
                        }
                    } else {
                        // Email provided, need phone and names
                        setLoginData(prev => ({ ...prev, email: loginData.identifier }));
                        setLoginStep('register-email');
                    }
                }
            } else {
                setError(data.error || 'Login failed');
            }
        } catch (error) {
            console.error('Login error:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Login failed';
            setError(errorMessage);
        }
    };



    const handleVerificationSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/auction/login/verify', {
                identifier: loginData.identifier,
                verificationCode: loginData.verificationCode
            });

            const data = response.data;

            if (response.status === 200 || response.status === 201) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                login(data.user, data.token, true); // Pass noRedirect: true
                setShowLoginModal(false);
                resetLoginState();

                // Execute pending action if any
                if (pendingAction === 'bid') {
                    setShowBidModal(true);
                } else if (pendingAction === 'buyNow') {
                    setShowBuyNowModal(true);
                }
            } else {
                setError(data.error || 'Verification failed');
            }
        } catch (error) {
            console.error('Verification error:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Verification failed';
            setError(errorMessage);
        }
    };

    const handleNewPhoneVerificationSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/verify-phone/confirm', {
                phone: loginData.phone,
                verificationCode: loginData.verificationCode
            });

            const data = response.data;

            if (response.status === 200 || response.status === 201) {
                // Phone verified successfully, now ask for email and names
                setLoginStep('register-phone');
                setError(''); // Clear any previous errors
            } else {
                setError(data.error || 'Verification failed');
            }
        } catch (error) {
            console.error('Verification error:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Verification failed';
            setError(errorMessage);
        }
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/auction/register', {
                email: loginData.email,
                phone: loginData.phone,
                firstName: loginData.firstName,
                lastName: loginData.lastName
            });

            const data = response.data;

            if (response.status === 200 || response.status === 201) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                login(data.user, data.token, true); // Pass noRedirect: true
                setShowLoginModal(false);
                resetLoginState();

                // Execute pending action if any
                if (pendingAction === 'bid') {
                    setShowBidModal(true);
                } else if (pendingAction === 'buyNow') {
                    setShowBuyNowModal(true);
                }
            } else {
                setError(data.error || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Registration failed';
            setError(errorMessage);
        }
    };

    const handleEmailRegistrationPhoneSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/verify-phone', {
                phone: loginData.phone
            });

            const data = response.data;

            if (response.status === 200) {
                setLoginStep('verify-registration-phone');
                setError(''); // Clear any previous errors
            } else {
                setError(data.error || 'Failed to send verification code');
            }
        } catch (error) {
            console.error('Phone verification error:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to send verification code';
            setError(errorMessage);
        }
    };

    const handleRegistrationPhoneVerificationSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/verify-phone/confirm', {
                phone: loginData.phone,
                verificationCode: loginData.verificationCode
            });

            const data = response.data;

            if (response.status === 200) {
                // Phone verified successfully, now complete registration
                await handleRegisterSubmit(e);
            } else {
                setError(data.error || 'Verification failed');
            }
        } catch (error) {
            console.error('Registration phone verification error:', error);
            // Show the actual server error message if available
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Verification failed';
            setError(errorMessage);
        }
    };

    const resetLoginState = () => {
        setLoginStep('identifier');
        setLoginData({
            identifier: '',
            verificationCode: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: ''
        });
        setLoginResponse(null);
        // Don't clear pendingAction here - it will be cleared after the action is completed
        setError('');
        rebindAuctionButtons();
    };

    const handleBackToIdentifier = () => {
        setLoginStep('identifier');
        setError('');
    };

    const formatTimeRemaining = (milliseconds) => {
        if (!milliseconds || milliseconds <= 0) return 'Auction ended';

        const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
        const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) return `${days} days, ${hours} hours`;
        if (hours > 0) return `${hours} hours, ${minutes} minutes`;
        return `${minutes} minutes`;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error && !auction) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Auction Not Found</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    if (!auction) return null;

    return (
        <div className="min-h-screen">
            {/* Success/Error Messages */}
            {success && (
                <div className="fixed top-4 right-4 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    {success}
                    <button onClick={() => setSuccess('')} className="ml-2 text-green-900">×</button>
                </div>
            )}

            {error && (
                <div className="fixed top-4 right-4 z-50 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                    <button onClick={() => setError('')} className="ml-2 text-red-900">×</button>
                </div>
            )}


            {/* Main Auction Content */}
            <div className="auction-content">
                <div dangerouslySetInnerHTML={{ __html: auction.richTextContent }} />
            </div>

            {/* Auction Status Bar */}
            <div
                className="text-white py-4"
                style={{ backgroundColor: config.colors.primary }}
            >
                <div className="max-w-6xl mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                            <DollarSign className="w-4 h-4 md:w-5 md:h-5" />
                            <div>
                                <div className="text-xs md:text-sm opacity-80">Current Highest Bid</div>
                                <div className="font-bold text-sm md:text-base">
                                    {auction.currentHighestBid > 0 ? `$${auction.currentHighestBid.toLocaleString()}` : 'No bids yet'}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                            <Gavel className="w-4 h-4 md:w-5 md:h-5" />
                            <div>
                                <div className="text-xs md:text-sm opacity-80">Next Minimum Bid</div>
                                <div className="font-bold text-sm md:text-base">${auction.nextMinimumBid?.toLocaleString()}</div>
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                            <Users className="w-4 h-4 md:w-5 md:h-5" />
                            <div>
                                <div className="text-xs md:text-sm opacity-80">Total Bids</div>
                                <div className="font-bold text-sm md:text-base">{auction.totalBids}</div>
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-1 md:gap-2">
                            <Clock className="w-4 h-4 md:w-5 md:h-5" />
                            <div>
                                <div className="text-xs md:text-sm opacity-80">Time Remaining</div>
                                <div className="font-bold text-sm md:text-base">{formatTimeRemaining(timeRemaining)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bid Modal */}
            {showBidModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-xl font-bold mb-4">Place Your Bid</h3>

                        <div className="mb-4">
                            <p className="text-gray-600 mb-2">
                                Current highest bid: <span className="font-semibold">
                                    {auction.currentHighestBid > 0 ? `$${auction.currentHighestBid.toLocaleString()}` : 'No bids yet'}
                                </span>
                            </p>
                            <p className="text-gray-600 mb-4">
                                Minimum bid required: <span className="font-semibold text-blue-600">
                                    ${auction.nextMinimumBid?.toLocaleString()}
                                </span>
                            </p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Your Bid Amount ($)</label>
                            <input
                                type="number"
                                value={bidAmount}
                                onChange={(e) => {
                                    setBidAmount(e.target.value);
                                    bidAmountSetByUser.current = true;
                                }}
                                min={auction.nextMinimumBid}
                                max={auction.maximumBid}
                                step={auction.bidIncrement}
                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder={auction.nextMinimumBid?.toLocaleString()}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Bid increment: ${auction.bidIncrement?.toLocaleString()}
                            </p>
                        </div>

                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-blue-800 text-sm font-medium">
                                💳 NO PAYMENT REQUIRED! This is just a bid request. No money will be charged.
                            </p>
                        </div>

                        {auction.adminApprovalRequired && (
                            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-yellow-800 text-sm">
                                    ⚠️ All bids require admin approval before becoming active.
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={handlePlaceBid}
                                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
                                disabled={!bidAmount || parseInt(bidAmount) < auction.nextMinimumBid}
                            >
                                {user ? 'Place Bid' : 'Continue to Login'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowBidModal(false);
                                    rebindAuctionButtons();
                                }}
                                className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Buy Now Modal */}
            {showBuyNowModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-xl font-bold mb-4 text-green-600">Buy It Now</h3>

                        <div className="mb-6">
                            <p className="text-gray-600 mb-4">
                                You are about to purchase this auction immediately for:
                            </p>
                            <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="text-3xl font-bold text-green-600">
                                    ${auction.buyNowPrice?.toLocaleString()}
                                </div>
                            </div>
                        </div>

                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-blue-800 text-sm font-medium">
                                💳 NO PAYMENT REQUIRED! This is just a purchase request. No money will be charged.
                            </p>
                        </div>

                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-yellow-800 text-sm">
                                ⚠️ This purchase requires admin approval. You will be contacted shortly to finalize the transaction.
                            </p>
                        </div>

                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                            <p className="text-red-800 text-sm font-medium">
                                ⚡ URGENT: This will end the auction immediately and prevent all other bids.
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleBuyNow}
                                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                            >
                                <ShoppingCart className="w-5 h-5" />
                                {user ? 'Submit Request' : 'Continue to Login'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowBuyNowModal(false);
                                    rebindAuctionButtons();
                                }}
                                className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* New Auction Login Modal */}
            {showLoginModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold">
                                {loginStep === 'identifier' && 'Login or Register'}
                                {loginStep === 'verification' && 'Enter Verification Code'}
                                {loginStep === 'verify-new-phone' && 'Verify Your Phone'}
                                {loginStep === 'verify-registration-phone' && 'Verify Your Phone'}
                                {(loginStep === 'register-phone' || loginStep === 'register-email') && 'Complete Registration'}
                            </h3>
                            {loginStep !== 'identifier' && (
                                <button
                                    onClick={handleBackToIdentifier}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                            )}
                        </div>

                        {/* Step indicator */}
                        <div className="mb-4">
                            <div className="flex items-center justify-center space-x-2">
                                <div className={`w-3 h-3 rounded-full ${loginStep === 'identifier' ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                                <div className={`w-3 h-3 rounded-full ${['verification', 'verify-new-phone', 'verify-registration-phone', 'register-phone', 'register-email'].includes(loginStep) ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                            </div>
                        </div>

                        {/* Pending Action Notice */}
                        {pendingAction && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                                <p className="text-blue-800 text-sm">
                                    {pendingAction === 'bid' && '🔄 You\'re about to place a bid after login'}
                                    {pendingAction === 'buyNow' && '🔄 You\'re about to purchase this auction after login'}
                                </p>
                            </div>
                        )}

                        {/* Identifier Step */}
                        {loginStep === 'identifier' && (
                            <form onSubmit={handleIdentifierSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Email or Phone Number</label>
                                    <input
                                        type="text"
                                        value={loginData.identifier}
                                        onChange={(e) => setLoginData({ ...loginData, identifier: e.target.value })}
                                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter your email or phone number"
                                        required
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
                                    >
                                        Continue
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowLoginModal(false);
                                            resetLoginState();
                                            setPendingAction(null); // Clear pending action when canceling
                                            bidAmountSetByUser.current = false; // Reset bid amount tracking
                                            rebindAuctionButtons();
                                        }}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        )}



                        {/* Verification Step */}
                        {loginStep === 'verification' && (
                            <form onSubmit={handleVerificationSubmit} className="space-y-4">
                                <div>
                                    <p className="text-gray-600 mb-4">
                                        We've sent a verification code to <strong>{loginData.identifier}</strong>
                                    </p>
                                    <label className="block text-sm font-medium mb-2">Verification Code</label>
                                    <input
                                        type="text"
                                        value={loginData.verificationCode}
                                        onChange={(e) => setLoginData({ ...loginData, verificationCode: e.target.value })}
                                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter 6-digit code"
                                        maxLength="6"
                                        required
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
                                    >
                                        Verify & Login
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleBackToIdentifier}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                                    >
                                        Back
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* New Phone Verification Step */}
                        {loginStep === 'verify-new-phone' && (
                            <form onSubmit={handleNewPhoneVerificationSubmit} className="space-y-4">
                                <div>
                                    <p className="text-gray-600 mb-4">
                                        We've sent a verification code to <strong>{loginData.phone}</strong> to verify your phone number.
                                    </p>
                                    <label className="block text-sm font-medium mb-2">Verification Code</label>
                                    <input
                                        type="text"
                                        value={loginData.verificationCode}
                                        onChange={(e) => setLoginData({ ...loginData, verificationCode: e.target.value })}
                                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter 6-digit code"
                                        maxLength="6"
                                        required
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
                                    >
                                        Verify Phone
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleBackToIdentifier}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                                    >
                                        Back
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Registration Step - Phone provided */}
                        {loginStep === 'register-phone' && (
                            <form onSubmit={handleRegisterSubmit} className="space-y-4">
                                <div>
                                    <p className="text-gray-600 mb-4">
                                        Please complete your registration. We have your phone number: <strong>{loginData.phone}</strong>
                                    </p>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            value={loginData.email}
                                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium mb-2">First Name</label>
                                        <input
                                            type="text"
                                            value={loginData.firstName}
                                            onChange={(e) => setLoginData({ ...loginData, firstName: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium mb-2">Last Name</label>
                                        <input
                                            type="text"
                                            value={loginData.lastName}
                                            onChange={(e) => setLoginData({ ...loginData, lastName: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
                                    >
                                        Complete Registration
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleBackToIdentifier}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                                    >
                                        Back
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Registration Step - Email provided */}
                        {loginStep === 'register-email' && (
                            <form onSubmit={handleEmailRegistrationPhoneSubmit} className="space-y-4">
                                <div>
                                    <p className="text-gray-600 mb-4">
                                        Please complete your registration. We have your email: <strong>{loginData.email}</strong>
                                    </p>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium mb-2">Phone Number</label>
                                        <input
                                            type="tel"
                                            value={loginData.phone}
                                            onChange={(e) => setLoginData({ ...loginData, phone: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter your phone number"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium mb-2">First Name</label>
                                        <input
                                            type="text"
                                            value={loginData.firstName}
                                            onChange={(e) => setLoginData({ ...loginData, firstName: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium mb-2">Last Name</label>
                                        <input
                                            type="text"
                                            value={loginData.lastName}
                                            onChange={(e) => setLoginData({ ...loginData, lastName: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
                                    >
                                        Send Verification Code
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleBackToIdentifier}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                                    >
                                        Back
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Registration Phone Verification Step */}
                        {loginStep === 'verify-registration-phone' && (
                            <form onSubmit={handleRegistrationPhoneVerificationSubmit} className="space-y-4">
                                <div>
                                    <p className="text-gray-600 mb-4">
                                        We've sent a verification code to <strong>{loginData.phone}</strong> to verify your phone number.
                                    </p>
                                    <label className="block text-sm font-medium mb-2">Verification Code</label>
                                    <input
                                        type="text"
                                        value={loginData.verificationCode}
                                        onChange={(e) => setLoginData({ ...loginData, verificationCode: e.target.value })}
                                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter 6-digit code"
                                        maxLength="6"
                                        required
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium"
                                    >
                                        Verify Phone
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleBackToIdentifier}
                                        className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400 font-medium"
                                    >
                                        Back
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuctionPage;